<template>
  <div class="field-wrapper">
    <div class="field-scheduler-item">
      <div class="days-wrapper" :class="field.additionalClass">
        <!--START: All Days-->
        <ToggleButton
          v-if="field.hasFixedDays"
          class="active-days-toggle"
          :field="selectAllDays"
          toggleName="active-days-toggle"
          @fieldChanged="fieldChanged"
        ></ToggleButton>
        <!--END: All Days-->

        <!--START: Same Time -->
        <div v-if="selectAllDays.value" class="same-time-wrapper">
          <!--START: Days-->
          <div class="active-days">
            <button
              class="btn btn-border active-day"
              :class="{
                active: isEnabledDay(day) && selectAllDays.value & day.isActive,
              }"
              v-for="(day, index) in overallDays.days"
              :key="index"
              @click="toggleDay(index)"
            >
              {{ day.name }}
            </button>
          </div>
          <!--END: Days-->

          <div class="overall-time-selector">
            <label v-if="!field.hideLabel" class="time-selector-label"
              >Set Available Hours</label
            >
            <div
              class="time-slot"
              v-for="(slot, index) in overallDays.timeSlots"
              :key="slot.id"
            >
              <FormBuilder
                class="time-slot-form"
                :fields="slot"
                @fieldChanged="fieldChanged"
                @input="fieldChanged"
              ></FormBuilder>
              <div class="btn-slot-wrapper">
                <unicon
                  class="btn-slot"
                  name="plus"
                  @click="addOverallTimeSlot(index)"
                ></unicon>
                <unicon
                  v-if="overallDays.timeSlots.length > 1"
                  class="btn-slot"
                  name="minus"
                  @click="removeOverallTimeSlot(index)"
                ></unicon>
              </div>
            </div>
          </div>
        </div>
        <!--END: Same Time -->

        <!--START: Unique Times-->
        <div v-else class="unique-times">
          <label v-if="!field.hideLabel" class="time-selector-label"
            >Set Available Hours</label
          >
          <div
            class="day-item"
            v-for="(day, dayIndex) in uniqueSlots"
            :key="dayIndex"
          >
            <div class="day-info">
              <label>{{ day.day }}</label>
              <ToggleButton
                class="specific-day-toggle"
                :field="day.isActive"
                toggleName="specific-day-toggle"
                @fieldChanged="dayToggled(dayIndex)"
                @input="fieldChanged"
              ></ToggleButton>
            </div>
            <div v-if="day.isActive.value" class="day-slots-wrapper">
              <div
                class="time-slot"
                v-for="(slot, index) in day.timeSlots"
                :key="slot.id"
              >
                <FormBuilder
                  class="time-slot-form"
                  :fields="slot"
                  @fieldChanged="fieldChanged"
                  @input="fieldChanged"
                ></FormBuilder>
                <div class="btn-slot-wrapper">
                  <unicon
                    class="btn-slot"
                    name="plus"
                    @click="addTimeSlot(dayIndex, index)"
                  ></unicon>
                  <unicon
                    v-if="day.timeSlots.length > 1"
                    class="btn-slot"
                    name="minus"
                    @click="removeTimeSlot(dayIndex, index)"
                  ></unicon>
                </div>
              </div>
            </div>
            <div v-else class="inactive-day">
              <span>Closed</span>
            </div>
          </div>
        </div>
        <!--END: Unique Times-->
      </div>
    </div>
  </div>
</template>

<script>
//Import libraries
import _ from "lodash";

//Import components
import ToggleButton from "./ToggleButton";

export default {
  name: "SchedulerField",
  props: {
    field: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      initOfferingPopulated: false,
      initComplete: false,
      overallDays: {
        days: [
          { name: "Mon", isActive: true },
          { name: "Tue", isActive: true },
          { name: "Wed", isActive: true },
          { name: "Thu", isActive: true },
          { name: "Fri", isActive: true },
          { name: "Sat", isActive: false },
          { name: "Sun", isActive: false },
        ],
        timeSlots: [],
      },
      selectAllDays: {
        type: "toggle",
        value: true,
        activeText: "Same Time Every Day",
        inactiveText: "Custom Times",
        additionalClass: "large-toggle",
      },
      uniqueSlots: [
        {
          day: "Monday",
          isActive: {
            type: "toggle",
            value: true,
            activeText: "Open",
          },
          timeSlots: [],
        },
        {
          day: "Tuesday",
          isActive: {
            type: "toggle",
            value: true,
            activeText: "Open",
          },
          timeSlots: [],
        },
        {
          day: "Wednesday",
          isActive: {
            type: "toggle",
            value: true,
            activeText: "Open",
          },
          timeSlots: [],
        },
        {
          day: "Thursday",
          isActive: {
            type: "toggle",
            value: true,
            activeText: "Open",
          },
          timeSlots: [],
        },
        {
          day: "Friday",
          isActive: {
            type: "toggle",
            value: true,
            activeText: "Open",
          },
          timeSlots: [],
        },
        {
          day: "Saturday",
          isActive: {
            type: "toggle",
            value: false,
            activeText: "Open",
          },
          timeSlots: [],
        },
        {
          day: "Sunday",
          isActive: {
            type: "toggle",
            value: false,
            activeText: "Open",
          },
          timeSlots: [],
        },
      ],
      timeSlotFields: {
        timeSlot: {
          type: "group",
          nestFields: false,
          fields: {
            startTime: {
              type: "time",
              placeholder: "Start time",
              required: true,
              hasError: false,
              value: "",
            },
            endTime: {
              type: "time",
              placeholder: "End time",
              required: true,
              hasError: false,
              value: "",
            },
          },
        },
      },
    };
  },
  watch: {
    overallDays: {
      deep: true,
      handler: function () {
        if (this.initComplete) this.fieldChanged();
      },
    },
    uniqueSlots: {
      deep: true,
      handler: function () {
        if (this.initComplete) this.fieldChanged();
      },
    },

    field: {
      immediate: true,
      deep: true,
      handler: function () {
        if (!this.initOfferingPopulated && this.field.value) {
          this.populateSlots();
        }
      },
    },
  },
  components: {
    FormBuilder: () => import("./FormBuilder"),
    ToggleButton,
  },
  created() {
    if (this.field.value) {
      this.field.overallDays = this.overallDays;
      this.field.uniqueSlots = this.uniqueSlots;
    } else this.initTimeSlots();

    this.field.selectAllDays = this.selectAllDays;
    this.selectAllDays.value = this.field.showEveryDay
      ? false
      : this.field.value.hasFixedDays;
  },
  computed: {
    activeDays: function () {
      let days = this.overallDays.days;
      if (this.field.disabledDays) {
        days = this.overallDays.days.filter(
          (day) => !this.field.disabledDays.includes(day)
        );
      }
      return days;
    },
  },
  methods: {
    populateSlots() {
      //Extract existing offering data
      this.selectAllDays.value = this.field.showEveryDay
        ? false
        : this.field.value.hasFixedDays;
      let offeringTimeSlots = this.field.value.days;
      let daysSlots = offeringTimeSlots[0].timeSlots;

      //START: Create the temporary time slot
      const timeSlot = this.createTimeSlot();
      this.timeSlotFields.timeSlot.fields.startTime.fields = timeSlot;
      this.timeSlotFields.timeSlot.fields.endTime.fields = timeSlot;

      let timeSlotData = this.timeSlotFields;
      //END: Create the temporary time slot

      let tempTimeSlot;
      //Create fixed days slots
      if (this.selectAllDays.value) {
        this.overallDays.timeSlots = [];

        for (let i = 0; i < daysSlots.length; i++) {
          tempTimeSlot = _.cloneDeep(timeSlotData);
          tempTimeSlot.timeSlot.fields.startTime.value = daysSlots[i].startTime;
          tempTimeSlot.timeSlot.fields.endTime.value = daysSlots[i].endTime;

          this.overallDays.timeSlots.push(_.cloneDeep(tempTimeSlot));
        }
      }

      //Create unique days slots
      else {
        for (let i = 0; i < this.uniqueSlots.length; i++) {
          this.uniqueSlots[i].timeSlots.splice(0);
          this.uniqueSlots[i].isActive.value = offeringTimeSlots[i].isActive;

          //Populate the unique days time slots
          if (offeringTimeSlots[i].timeSlots.length)
            for (let j = 0; j < offeringTimeSlots[i].timeSlots.length; j++) {
              tempTimeSlot = _.cloneDeep(timeSlotData);
              tempTimeSlot.timeSlot.fields.startTime.value =
                offeringTimeSlots[i].timeSlots[j].startTime;
              tempTimeSlot.timeSlot.fields.endTime.value =
                offeringTimeSlots[i].timeSlots[j].endTime;

              this.uniqueSlots[i].timeSlots.push(_.cloneDeep(tempTimeSlot));
            }
          else {
            this.uniqueSlots[i].timeSlots.push(
              _.cloneDeep(this.timeSlotFields)
            );
          }
        }
      }
    },

    initTimeSlots() {
      const timeSlot = this.createTimeSlot();
      this.timeSlotFields.timeSlot.fields.startTime.fields = timeSlot;
      this.timeSlotFields.timeSlot.fields.endTime.fields = timeSlot;

      let timeSlotData = this.timeSlotFields;

      let tempTimeSlot;
      this.uniqueSlots.forEach(function (day) {
        tempTimeSlot = _.cloneDeep(timeSlotData);

        if (!day.isActive.value) {
          tempTimeSlot.timeSlot.fields.startTime.required = false;
          tempTimeSlot.timeSlot.fields.endTime.required = false;
        }
        day.timeSlots.push(_.cloneDeep(tempTimeSlot));
      });

      this.overallDays.timeSlots.push(_.cloneDeep(this.timeSlotFields));

      this.field.overallDays = this.overallDays;
      this.field.uniqueSlots = this.uniqueSlots;
      this.field.selectAllDays = this.selectAllDays;

      setTimeout(() => (this.initComplete = true), 0);
    },

    createTimeSlot() {
      var hours, minutes, ampm;
      var time = [];
      for (var i = 0; i <= 1440; i += 15) {
        hours = Math.floor(i / 60);
        minutes = i % 60;
        if (minutes < 10) {
          minutes = "0" + minutes;
        }
        ampm = hours % 24 < 12 ? "AM" : "PM";
        hours = hours % 12;
        if (hours === 0) {
          hours = 12;
        }

        time.push({
          name: hours + ":" + minutes + " " + ampm,
          value: hours + ":" + minutes + " " + ampm,
        });
      }

      return time;
    },

    isEnabledDay(day) {
      let isEnabledDay = true;
      if (
        this.field.disabledDays &&
        this.field.disabledDays.some((res) => res.includes(day))
      ) {
        isEnabledDay = false;
      }

      return isEnabledDay;
    },

    dayToggled(index) {
      const isActive = this.uniqueSlots[index].isActive.value;
      this.uniqueSlots[index].timeSlots.forEach(function (day) {
        day.timeSlot.fields.startTime.required = isActive;
        day.timeSlot.fields.endTime.required = isActive;
      });
      this.fieldChanged();
    },

    addOverallTimeSlot(index) {
      this.overallDays.timeSlots.splice(
        index + 1,
        0,
        _.cloneDeep(this.timeSlotFields)
      );
      this.fieldChanged();
    },

    removeOverallTimeSlot(index) {
      this.overallDays.timeSlots.splice(index, 1);
      this.fieldChanged();
    },

    addTimeSlot(dayIndex, index) {
      this.uniqueSlots[dayIndex].timeSlots.splice(
        index + 1,
        0,
        _.cloneDeep(this.timeSlotFields)
      );
      this.fieldChanged();
    },

    removeTimeSlot(dayIndex, index) {
      this.uniqueSlots[dayIndex].timeSlots.splice(index, 1);
      this.fieldChanged();
    },

    toggleDay(index) {
      this.overallDays.days[index].isActive =
        !this.overallDays.days[index].isActive;
      this.fieldChanged();
    },

    fieldChanged() {
      this.field.overallDays = this.overallDays;
      this.field.uniqueSlots = this.uniqueSlots;
      this.field.selectAllDays = this.selectAllDays;

      if (!this.initOfferingPopulated) this.initOfferingPopulated = true;

      this.$emit("fieldChanged");
    },
  },
};
</script>

<style scoped lang="scss">
.active-days-toggle.field-toggle-item {
  flex-direction: row !important;

  /deep/ {
    label {
      margin-left: 0.75rem !important;
    }
  }
}

.active-days {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 1rem;

  .active-day {
    margin-right: 0.5rem;
    padding: 0.5rem;

    &:hover,
    &.active {
      background-color: $blackColor;
      color: $whiteColor;
    }
  }
}

input[readonly] {
  cursor: default;
  border: 1px solid #e7e6ef;
}

.time-selector-label {
  display: block;
  font-size: $normalFontSize;
  font-weight: $mediumFontWeight;
  opacity: 0.75;
  margin: 2rem 0 1rem;
}

.unique-times {
  .time-selector-label {
    margin: 1rem 0 2rem;
  }
}

.time-slot {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 0.5rem;

  label {
    width: 4rem;
  }

  .btn {
    margin-left: 0.5rem;
  }

  /deep/ {
    .time-slot-form {
      width: 20rem;
      margin: 0 1rem 0 0;

      .field-group-wrapper,
      .field-main-item {
        margin-bottom: 0;
      }
    }
  }
}

.day-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 1rem;

  .day-info {
    min-width: 9rem;
    margin-right: 4rem;

    label {
      display: block;
      color: $blackColor;
      font-weight: $mediumFontWeight;
      margin-bottom: 0.75rem;
    }
  }

  .specific-day-toggle {
    flex-direction: row !important;
    margin: 0.5rem 0 0 !important;

    /deep/ {
      label {
        font-size: $smallFontSize !important;
        margin-left: 0.75rem;
      }
    }
  }
}

.btn-slot {
  /deep/ svg {
    fill: $titleColor;
    width: $largeFontSize;
    cursor: pointer;
    opacity: 0.45;

    &:hover {
      opacity: 1;
      fill: $secondaryColor;
    }
  }
  margin: 0 0.5rem;
}

.large-scheduler {
  .day-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 16rem;
    margin-top: 0.5rem;

    label {
      margin-bottom: 0;
      width: 10rem;
    }

    .specific-day-toggle {
      margin-top: 0 !important;
    }
  }

  .day-slots-wrapper {
    flex: 1;
  }

  .time-slot {
    justify-content: flex-end;

    &:first-child {
      margin-top: 0;
    }
  }

  .day-item {
    padding-bottom: 1rem;
    border-bottom: 1px solid $greyBorderColor;
  }
}

.inactive-day {
  flex: 1;
  text-align: center;

  span {
    display: block;
    color: $greyColor;
    margin: 0.5rem 0;
    opacity: 0.5;
  }
}

.btn-slot-wrapper {
  width: 4.5rem;
}

//Mobile Styles
@media screen and (max-width: $mobileWidth) {
  .active-days {
    .btn {
      font-size: 0;

      &:first-letter {
        font-size: 1rem;
      }
    }
  }

  .day-item {
    display: block;

    .day-info {
      margin-bottom: 1rem;
    }
  }

  .time-slot {
    display: block;

    .time-slot-form {
      width: auto;
      margin-bottom: -1rem;
    }

    .btn {
      display: inline-block;
      margin: 0 0.5rem 1.5rem 0;
    }
  }
}
</style>
